import z from 'zod';
import { DEFAULT_SPARK_OPTIONS } from './constants';
import {
  ANALYTICS_EVENT_NAME_KEYS,
  type AnalyticsProviderConfig,
  CHECKOUT_BOOLEAN_ATTRIBUTES,
  CHECKOUT_STRING_ATTRIBUTES,
  type CheckoutDropdownGroup,
  type CheckoutDropdownOptionItem,
  type CheckoutElement,
  type CustomCheckoutElementAttributes,
  EDITABLE_OPTION_KEYS,
  isOfficialAnalyticsProviderName,
  isStandardCheckoutElementId,
  type LocaleEntry,
  type LocaleGroup,
  type OfficialAnalyticsProviderName,
  type SparkOptions,
  type StandardCheckoutElement,
} from './types/spark-options';
import { normalizeAccountSectionConfiguration } from './utils/account-sections';
import { isPlainObject, makeMembershipGuard } from './utils/object';
import { normalizePaymentMethodsOrder } from './utils/payment-methods';
import { parseSparkOptionsSnippet } from './utils/snippet';
import { fromSparkJsOptions } from './utils/sparkjs-options';
import { childPath, ImportWarnings, topLevelKey } from './utils/warnings';

type StandardCheckoutElementEntry = Extract<CheckoutElement, { kind: 'standard' }>;
type CustomCheckoutElementEntry = Extract<CheckoutElement, { kind: 'custom' }>;

const DEFAULT_STANDARD_CHECKOUT_ELEMENTS = DEFAULT_SPARK_OPTIONS.checkoutCustomElements.filter(
  (element): element is StandardCheckoutElementEntry => element.kind === 'standard',
);
const DEFAULT_STANDARD_CHECKOUT_ELEMENT_BY_ID = new Map(
  DEFAULT_STANDARD_CHECKOUT_ELEMENTS.map((element) => [element.id, element]),
);

const EDITABLE_OPTION_KEY_SET = new Set<string>(EDITABLE_OPTION_KEYS);

/** Locale untranslated custom checkout field title/detail are filed under; moved by hand later. */
const FALLBACK_TRANSLATION_LOCALE = 'en';

const storedSparkOptionsSchema = z.record(z.string(), z.unknown());

const isAnalyticsEventName = makeMembershipGuard(ANALYTICS_EVENT_NAME_KEYS);

function normalizeAnalyticsHandler(handler: unknown): OfficialAnalyticsProviderName | null {
  return isOfficialAnalyticsProviderName(handler) ? handler : null;
}

function deepMerge(base: unknown, patch: unknown): unknown {
  if (Array.isArray(base)) {
    return Array.isArray(patch) ? patch : base;
  }

  if (isPlainObject(base)) {
    if (!isPlainObject(patch)) return base;
    const out: Record<string, unknown> = { ...base };
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) continue;
      out[key] = key in out ? deepMerge(out[key], value) : value;
    }
    return out;
  }

  return patch;
}

const SECTION_CONFIGURATION_PATH = childPath(
  childPath('display', 'sectionConfiguration'),
  'my-account',
);

function normalizeSectionConfiguration(
  sectionConfiguration: SparkOptions['display']['sectionConfiguration'],
  warnings?: ImportWarnings,
): SparkOptions['display']['sectionConfiguration'] {
  return {
    ...sectionConfiguration,
    'my-account': normalizeAccountSectionConfiguration(
      sectionConfiguration['my-account'],
      warnings &&
        ((field: 'order' | 'hide', index: number) =>
          warnings.add(childPath(childPath(SECTION_CONFIGURATION_PATH, field), index))),
    ),
  };
}

/** Ids are positional here; nothing is mid-edit at load, import or reset. */
function normalizeLocaleGroups(value: unknown): LocaleGroup[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: LocaleGroup[] = [];
  for (const item of value) {
    if (!isPlainObject(item) || typeof item.locale !== 'string') {
      continue;
    }
    const entries: LocaleEntry[] = [];
    if (Array.isArray(item.entries)) {
      for (const entry of item.entries) {
        if (
          isPlainObject(entry) &&
          typeof entry.key === 'string' &&
          typeof entry.value === 'string'
        ) {
          entries.push({ id: entries.length, key: entry.key, value: entry.value });
        }
      }
    }
    out.push({ id: out.length, locale: item.locale, entries });
  }
  return out;
}

function normalizeCheckoutAttributes(
  value: unknown,
  warnings?: ImportWarnings,
  path?: string,
): CustomCheckoutElementAttributes {
  const out: CustomCheckoutElementAttributes = { type: 'text' };
  if (!isPlainObject(value)) {
    return out;
  }
  const drop = (key: string) => warnings?.add(childPath(path ?? 'attributes', key));

  for (const key of CHECKOUT_STRING_ATTRIBUTES) {
    const raw = value[key];
    if (typeof raw === 'string') {
      if (raw !== '') out[key] = raw;
    } else if (raw !== undefined) {
      // A blank string is a deliberate clear; only a non-string is malformed.
      drop(key);
    }
  }
  for (const key of CHECKOUT_BOOLEAN_ATTRIBUTES) {
    const raw = value[key];
    if (typeof raw === 'boolean') {
      out[key] = raw;
    } else if (raw !== undefined) {
      drop(key);
    }
  }
  if (value.fileextensions !== undefined) {
    if (Array.isArray(value.fileextensions)) {
      const exts = value.fileextensions.filter((ext): ext is string => typeof ext === 'string');
      if (exts.length !== value.fileextensions.length) drop('fileextensions');
      if (exts.length > 0) out.fileextensions = exts;
    } else {
      drop('fileextensions');
    }
  }
  return out;
}

function normalizeNamedLocaleGroups(value: unknown, namedKey: string): LocaleGroup[] {
  return normalizeLocaleGroups(value).map((group) => ({
    ...group,
    entries: group.entries.filter((entry) => entry.key === namedKey),
  }));
}

function normalizeDropdownOptionItem(value: unknown): CheckoutDropdownOptionItem | null {
  if (!isPlainObject(value)) {
    return null;
  }
  return {
    value: typeof value.value === 'string' ? value.value : '',
    translations: normalizeNamedLocaleGroups(value.translations, 'label'),
  };
}

function normalizeDropdownOptions(value: unknown): CheckoutDropdownGroup[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const groups: CheckoutDropdownGroup[] = [];
  for (const raw of value) {
    if (!isPlainObject(raw) || !Array.isArray(raw.items)) {
      continue;
    }
    const items = raw.items
      .map(normalizeDropdownOptionItem)
      .filter((item): item is CheckoutDropdownOptionItem => item !== null);
    const group: CheckoutDropdownGroup = { items };
    const translations = normalizeNamedLocaleGroups(raw.translations, 'groupName');
    if (translations.length > 0) {
      group.translations = translations;
    }
    groups.push(group);
  }
  return groups.length > 0 ? groups : undefined;
}

function normalizeStandardCheckoutElement(
  element: Record<string, unknown>,
): StandardCheckoutElementEntry | null {
  if (!isStandardCheckoutElementId(element.id)) {
    return null;
  }

  const defaults = DEFAULT_STANDARD_CHECKOUT_ELEMENT_BY_ID.get(element.id);

  const entry: StandardCheckoutElementEntry = {
    kind: 'standard',
    id: element.id,
    required:
      typeof element.required === 'boolean' ? element.required : (defaults?.required ?? false),
    hidden: typeof element.hidden === 'boolean' ? element.hidden : false,
  };

  if (typeof element.disabled === 'boolean') {
    entry.disabled = element.disabled;
  }

  return entry;
}

/** File a custom field's plain-string title/detail (no translations) under `fallbackLocale`. */
function normalizeCustomCheckoutElement(
  element: Record<string, unknown>,
  fallbackLocale: string,
  warnings?: ImportWarnings,
  path?: string,
): CustomCheckoutElementEntry {
  const translations = normalizeLocaleGroups(element.translations);

  if (translations.length === 0) {
    const entries: LocaleEntry[] = [];
    if (typeof element.title === 'string') {
      entries.push({ id: entries.length, key: 'title', value: element.title });
    }
    if (typeof element.detail === 'string') {
      entries.push({ id: entries.length, key: 'detail', value: element.detail });
    }
    if (entries.length > 0) {
      translations.push({ id: 0, locale: fallbackLocale, entries });
    }
  }

  const entry: CustomCheckoutElementEntry = {
    kind: 'custom',
    name: typeof element.name === 'string' ? element.name : '',
    translations,
    attributes: normalizeCheckoutAttributes(
      element.attributes,
      warnings,
      path && childPath(path, 'attributes'),
    ),
  };

  const options = normalizeDropdownOptions(element.options);
  if (options) {
    entry.options = options;
  }

  return entry;
}

function normalizeCheckoutElements(
  checkoutCustomElements: unknown,
  fallbackLocale: string,
  warnings?: ImportWarnings,
): SparkOptions['checkoutCustomElements'] {
  const normalized: CheckoutElement[] = [];
  const importedStandardIds = new Set<StandardCheckoutElement>();

  if (Array.isArray(checkoutCustomElements)) {
    checkoutCustomElements.forEach((element, index) => {
      if (!isPlainObject(element)) {
        return;
      }
      const path = childPath('checkoutCustomElements', index);

      if (element.kind === 'standard') {
        const standardElement = normalizeStandardCheckoutElement(element);
        if (!standardElement) {
          return;
        }
        if (importedStandardIds.has(standardElement.id)) {
          // A second entry for the same standard field loses whatever it configured.
          warnings?.add(path);
          return;
        }

        normalized.push(standardElement);
        importedStandardIds.add(standardElement.id);
        return;
      }

      if (element.kind === 'custom') {
        normalized.push(normalizeCustomCheckoutElement(element, fallbackLocale, warnings, path));
      }
    });
  }

  for (const element of DEFAULT_STANDARD_CHECKOUT_ELEMENTS) {
    if (!importedStandardIds.has(element.id)) {
      normalized.push({ ...element, hidden: true });
    }
  }

  return normalized;
}

const ANALYTICS_PROVIDERS_PATH = childPath('analytics', 'providers');

function normalizeAnalyticsOptions(
  analytics: unknown,
  warnings?: ImportWarnings,
): SparkOptions['analytics'] {
  if (!isPlainObject(analytics)) {
    // `null` is the intentional "off" value; anything else was configured but unusable.
    if (analytics != null) warnings?.add('analytics');
    return null;
  }
  if (!Array.isArray(analytics.providers)) {
    warnings?.add(ANALYTICS_PROVIDERS_PATH);
    return null;
  }

  const providers: AnalyticsProviderConfig[] = [];
  const usedOfficialProviders = new Set<OfficialAnalyticsProviderName>();

  analytics.providers.forEach((provider, index) => {
    const providerPath = childPath(ANALYTICS_PROVIDERS_PATH, index);
    if (!isPlainObject(provider)) {
      warnings?.add(providerPath);
      return;
    }

    const handler = normalizeAnalyticsHandler(provider.handler);
    if (!handler) {
      warnings?.add(childPath(providerPath, 'handler'));
      return;
    }
    if (usedOfficialProviders.has(handler)) {
      warnings?.add(providerPath);
      return;
    }
    usedOfficialProviders.add(handler);

    const events: AnalyticsProviderConfig['events'] = {};
    if (isPlainObject(provider.events)) {
      const eventsPath = childPath(providerPath, 'events');
      for (const [eventName, enabled] of Object.entries(provider.events)) {
        if (isAnalyticsEventName(eventName) && typeof enabled === 'boolean') {
          events[eventName] = enabled;
        } else {
          warnings?.add(childPath(eventsPath, eventName));
        }
      }
    }

    providers.push({
      handler,
      events,
    });
  });

  return providers.length > 0 ? { providers } : null;
}

function normalizeSparkOptions(options: SparkOptions, warnings?: ImportWarnings): SparkOptions {
  return {
    ...options,
    translations: normalizeLocaleGroups(options.translations),
    display: {
      ...options.display,
      sectionConfiguration: normalizeSectionConfiguration(
        options.display.sectionConfiguration,
        warnings,
      ),
    },
    paymentMethodsOrder: normalizePaymentMethodsOrder(
      options.paymentMethodsOrder,
      warnings && ((index: number) => warnings.add(childPath('paymentMethodsOrder', index))),
    ),
    checkoutCustomElements: normalizeCheckoutElements(
      options.checkoutCustomElements,
      FALLBACK_TRANSLATION_LOCALE,
      warnings,
    ),
    analytics: normalizeAnalyticsOptions(options.analytics, warnings),
  };
}

export function parseStoredSparkOptions(raw: unknown): SparkOptions {
  const result = storedSparkOptionsSchema.safeParse(raw);
  const defaults = structuredClone(DEFAULT_SPARK_OPTIONS);
  if (!result.success) {
    return defaults;
  }
  return normalizeSparkOptions(
    deepMerge(defaults, fromSparkJsOptions(result.data)) as SparkOptions,
  );
}

function pickEditableOptionKeys(patch: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (EDITABLE_OPTION_KEY_SET.has(key)) {
      result[key] = value;
    }
  }
  return result;
}

export function applySparkOptionsPatch(
  current: SparkOptions,
  patch: unknown,
  warnings?: ImportWarnings,
): SparkOptions {
  const adoptedPatch = isPlainObject(patch)
    ? pickEditableOptionKeys(fromSparkJsOptions(patch))
    : patch;
  return normalizeSparkOptions(deepMerge(current, adoptedPatch) as SparkOptions, warnings);
}

export type SparkOptionsImportResult = {
  options: SparkOptions;
  /** Editable snippet paths dropped while importing; empty when everything came across cleanly. */
  warnings: string[];
};

/** Keep only drops the merchant can act on: paths under an editable option, each reported once. */
function editableImportWarnings(paths: readonly string[]): string[] {
  const editable = paths.filter((path) => EDITABLE_OPTION_KEY_SET.has(topLevelKey(path)));
  return [...new Set(editable)];
}

/** Parse a pasted snippet, merge its editable values onto `current`, and report anything dropped. */
export function importSparkOptionsSnippet(
  current: SparkOptions,
  snippet: string,
): SparkOptionsImportResult {
  const warnings = new ImportWarnings();
  const patch = parseSparkOptionsSnippet(snippet, warnings);
  const options = applySparkOptionsPatch(current, patch, warnings);
  return { options, warnings: editableImportWarnings(warnings.paths) };
}
